/*
 * Edit alert Form
 * Add and Edit alerts
 *
 */

import * as React from 'react';

import { Button, Col, Row } from 'react-bootstrap';
import { FieldConfig, FormArray, FormGenerator, FormGroup, Validators } from 'react-reactive-form';
import debounce from 'lodash/debounce';
import { buildJobHourPrefill } from './jobHourEditPrefill';

import { FormUtil } from '../common/FormUtil';
import { IjobHour, Ijob, IlaborRate, Ioption, Iuser, IWorkOrder, IjobWorkOrder } from '../../models';
import { IpartFormValues } from '../../modelsForms';
import { TFunction } from 'i18next';
import { constants } from '../../constants/constants';
import { initialJobHour, initialJob } from '../../reducers/initialState';
import { toastr } from 'react-redux-toastr';
import { AddHoursTableContainer } from './AddHoursTableContainer';
import {
    stageJobHour,
    saveStagedJobHours,
    clearStagedJobHours,
    setSelectedJobHourID,
    unlinkWorkOrders
} from '../../actions/manageJobActions';
import { jobTypesIdEnum } from '../../models-enums';

import { v4 as uuidv4 } from 'uuid';

interface Iprops {
    loading: boolean;
    colorButton: string;
    updateFormValue: (formValue: { [key: string]: any }) => any;
    setFormValues: (formValues: { [key: string]: any }) => any;
    t: TFunction;
    formValues: IpartFormValues;
    toggleModal: () => void;
    selectedJobHour: IjobHour;
    laborRates: IlaborRate[];
    disabled?: boolean;
    secondModal?: boolean;
    selectedJob: Ijob;
    fseOptions: Ioption[];
    fseUsers: Iuser[];
    currentUserID: string;
    stageJobHour: typeof stageJobHour;
    saveStagedJobHours: typeof saveStagedJobHours;
    clearStagedJobHours: typeof clearStagedJobHours;
    setSelectedJobHourID: typeof setSelectedJobHourID;
    hasStagedHours: boolean;
    onHide: () => void;
    onClose?: () => void;
    selectedWorkOrder: IWorkOrder | undefined;
    selectedJobWorkOrder: IjobWorkOrder | undefined;
    // deleteSAPJobWorkOrder: typeof deleteSAPJobWorkOrder;
    unlinkWorkOrders: typeof unlinkWorkOrders;
    isUnproductiveJob?: boolean;
    wideModal?: boolean;
}

interface State {
    fieldConfig: FieldConfig;
    showSAPWONum: boolean;
}

class EditHoursModalForm extends React.Component<Iprops, State> {
    private formGroup: FormGroup | any;
    private subscription: any;
    private updateFormValueDebounced: any;
    static defaultProps = {
        selectedJobHour: initialJobHour,
        colorButton: constants.colors[`blueButton`],
        selectedJob: initialJob
    };
    constructor(props: Iprops) {
        super(props);
        const showAddSAP =
            props.selectedJob.jobTypeID === jobTypesIdEnum.inspection ||
            props.selectedJob.jobTypeID === jobTypesIdEnum.agsRebalancing;

        this.state = {
            fieldConfig: this.buildFieldConfig(),
            showSAPWONum: showAddSAP
        };
        this.updateFormValueDebounced = debounce(this.props.updateFormValue, 300);
    }

    // since the modal is rendered in this component, this only runs when the ManageInventory view mounts
    componentDidMount() {
        // Start each modal session with an empty staging map (guards against stale
        // rows persisted from a prior session / different job).
        this.props.clearStagedJobHours();
        this.setState({
            fieldConfig: this.buildFieldConfig()
        });
    }

    componentDidUpdate(prevProps: Iprops) {
        if (this.props.selectedJobHour !== prevProps.selectedJobHour) {
            this.setState({ fieldConfig: this.buildFieldConfig() });
        }
    }

    componentWillUnmount() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.props.setFormValues({});
    }

    /*
     * formValuesToSelectedItem - convert the formValues to the shape of the selectedItem
     */
    formValuesToSelectedItem = () => {
        const formValues = FormUtil.getValues(this.formGroup.value);
        const fseUser = this.props.fseUsers.filter(user => user.id === formValues.engineerVanID);
        // formValues.description is the id of the labor rate
        const laborRateNum = this.props.laborRates.find(rate => rate.id === formValues.description);

        return {
            ...formValues,
            engineerVanID: fseUser[0]?.engineerVanID ?? '',
            sapEngineerID: fseUser[0]?.sapEngineerID ?? '',
            number: laborRateNum?.code,
            laborRateID: laborRateNum?.id,
            description: laborRateNum?.description,
            unitOfMeasure: laborRateNum?.unitOfMeasure
        };
    };

    buildFieldConfig = (defaultValues: IjobHour = this.props.selectedJobHour) => {
        const disabled = this.props.disabled === true;

        const laborRatesOptions = this.props.laborRates.map((rate: IlaborRate) => {
            return {
                value: rate.id,
                label: `${rate.code} - ${rate.description}`
            };
        });

        const prefill = buildJobHourPrefill(
            defaultValues,
            laborRatesOptions,
            this.props.fseOptions,
            this.props.fseUsers,
            this.props.currentUserID
        );

        const fieldConfigControls = {
            quantity: {
                render: FormUtil.NumericInput,
                meta: {
                    label: 'jobHoursForm.hoursLabel',
                    colWidth: 6,
                    name: 'quantity',
                    min: 0.01,
                    step: 0.01
                },
                options: {
                    validators: [FormUtil.validators.requiredWithTrim, FormUtil.validators.isValidPositiveFloat]
                },
                formState: { value: prefill.quantity, disabled }
            },
            date: {
                render: FormUtil.Datetime,
                meta: {
                    label: 'jobHoursForm.dateLabel',
                    colWidth: 6,
                    name: 'date'
                },
                formState: {
                    value: prefill.date,
                    disabled
                },
                options: {
                    validators: [FormUtil.validators.isValidMoment, FormUtil.validators.requiredWithTrim]
                }
            },
            description: {
                render: FormUtil.Select,
                meta: {
                    label: 'jobHoursForm.laborRatesLabel',
                    options: laborRatesOptions,
                    colWidth: 6,
                    type: 'text',
                    placeholder: 'common:searchPlaceholder',
                    isClearable: true,
                    disableSort: true // Preserve API usage-based sorting order
                },
                options: {
                    validators: [Validators.required]
                },
                formState: { value: prefill.description, disabled }
            },
            engineerVanID: {
                render: FormUtil.Select,
                meta: {
                    options: this.props.fseOptions,
                    label: 'jobHoursForm.engineerVanIdLabel',
                    name: 'engineerVanID',
                    colWidth: 6
                },
                options: {
                    validators: FormUtil.validators.requiredWithTrim
                },
                formState: {
                    value: prefill.engineerVanID,
                    disabled
                }
            }
        };
        return FormUtil.translateForm(
            {
                controls: { ...fieldConfigControls }
            },
            this.props.t
        );
    };

    handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.formGroup.status === 'INVALID') {
            this.formGroup.markAsSubmitted();
            toastr.error('Please check invalid inputs', '', constants.toastrError);
            return;
        }
        const cleanValues: any = this.formValuesToSelectedItem();

        const quantity = Number(cleanValues.quantity);
        if (cleanValues.number === 'ZAM015') {
            if (quantity > 10000) {
                toastr.error('Error', 'Aditional Expenses cannot exceed 10,000.', constants.toastrError);
                return;
            }
        } else if (cleanValues.unitOfMeasure?.trim().toLowerCase() === 'hr') {
            if (quantity > 99) {
                toastr.error('Error', 'Hours cannot exceed 99.', constants.toastrError);
                return;
            }
        }
        // Other units of measure (LCU, UA, KMT) have no upper limit

        if (this.props.selectedJobHour.id.length) {
            // UPDATE an already-staged row (same id replaces it in the staging map)
            this.props.stageJobHour({ ...this.props.selectedJobHour, ...cleanValues });
            this.props.setSelectedJobHourID('');
        } else {
            // ADD a new staged row
            const newJobHour: IjobHour = {
                ...initialJobHour,
                id: uuidv4(),
                jobID: this.props.selectedJob.id,
                ...cleanValues
            };
            this.props.stageJobHour(newJobHour);
        }
        // Reset the form back to add-mode defaults for the next entry
        this.setState({ fieldConfig: this.buildFieldConfig(initialJobHour) });
    };

    handleDone = () => {
        // If onClose is defined, override close state. Otherwise, set selectedJobHourID to empty string
        if (this.props.onClose) {
            this.props.onClose();
            this.props.setSelectedJobHourID('');
            return;
        }
        this.props.toggleModal();
        this.props.setSelectedJobHourID('');
    };

    handleSave = () => {
        // Submit all staged rows to the API in one batch, then close.
        this.props.saveStagedJobHours(this.props.selectedJob.id);
        this.handleDone();
    };

    handleCancel = () => {
        // Mid-edit of a staged row: discard the edit, return to add-mode (stay open).
        if (this.props.selectedJobHour.id.length) {
            this.props.setSelectedJobHourID('');
            this.setState({ fieldConfig: this.buildFieldConfig(initialJobHour) });
            return;
        }
        // Otherwise defer to the guarded close (warns if there are unsaved staged rows).
        this.props.onHide();
    };

    setForm = (form: FormGroup | FormArray) => {
        this.formGroup = form;
        this.formGroup.meta = {
            loading: this.props.loading
        };
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.subscription = this.formGroup.valueChanges.subscribe(() => {
            this.setState({}); // force re-render
        });
    };

    cleanForm = () => {
        this.formGroup.reset();
    };

    clearSelectedJobSAPWorkOrder = () => {
        if (this.props.selectedJobWorkOrder !== undefined) {
            // If we see that any hours have been saved to the api, show a confirmation dialog (this might require more complex logic later on)
            if (this.props.selectedJob.jobHours.length > 0) {
                const toastrConfirmOptions = {
                    onOk: () => {
                        // You shouldn't get this toast if selectedJobWorkOrder is undefined, but TS is complaining
                        if (this.props.selectedJobWorkOrder !== undefined) {
                            this.props.unlinkWorkOrders(this.props.t, [this.props.selectedJobWorkOrder]);
                        }
                    },
                    onCancel: () => null,
                    okText: 'Go Ahead',
                    cancelText: 'Cancel'
                };
                toastr.confirm(this.props.t('removeSAPWorkOrderWarning'), toastrConfirmOptions);
            } else {
                this.props.unlinkWorkOrders(this.props.t, [this.props.selectedJobWorkOrder]);
            }
        } else {
            toastr.error('Could not find the job work order', '', constants.toastrError);
        }
    };

    render() {
        const { t } = this.props;

        let isQuantityInvalid = true;
        if (this.formGroup && this.formGroup.get('quantity')) {
            const quantityField = this.formGroup.get('quantity');
            isQuantityInvalid = quantityField.invalid;
        }

        return (
            <form onSubmit={this.handleSubmit} className="clearfix beacon-form">
                <Col xs={12}>
                    <Row>
                        <FormGenerator
                            onUnmount={this.cleanForm}
                            onMount={this.setForm}
                            fieldConfig={this.state.fieldConfig}
                        />
                    </Row>
                </Col>
                <Col xs={12} className="d-flex justify-content-end mt-3 mb-3">
                    <Button variant={this.props.colorButton} type="submit" disabled={isQuantityInvalid}>
                        {this.props.selectedJobHour.id.length
                            ? String(t('common:update'))
                            : String(t('jobHoursForm.addButton'))}
                    </Button>
                </Col>

                {this.state.showSAPWONum && this.props.selectedWorkOrder !== undefined && (
                    <>
                        <Row>
                            <Col xs={12}>
                                <p
                                    style={{
                                        marginLeft: '20px'
                                    }}
                                >
                                    {String(t('selectedWorkOrder'))} {this.props.selectedWorkOrder.number}
                                </p>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Button
                                    variant="default"
                                    type="button"
                                    style={{
                                        marginLeft: '30px'
                                    }}
                                    onClick={this.clearSelectedJobSAPWorkOrder}
                                >
                                    {String(t('clear'))}
                                </Button>
                            </Col>
                        </Row>
                    </>
                )}

                <Row>
                    <Col xs={12}>
                        <AddHoursTableContainer
                            colorButton={this.props.colorButton}
                            currentTile={constants.getTileByURL(window.location.pathname)}
                            disabled={this.props.disabled}
                        />
                    </Col>
                </Row>

                <div className="form-buttons d-flex justify-content-between gap-2">
                    <Button variant="default" type="button" onClick={this.handleCancel}>
                        {String(t('common:cancel'))}
                    </Button>
                    <Button
                        variant={this.props.colorButton}
                        type="button"
                        onClick={this.handleSave}
                        disabled={!this.props.hasStagedHours}
                    >
                        {String(t('common:save'))}
                    </Button>
                </div>
            </form>
        );
    }
}

export default EditHoursModalForm;
